import { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { enqueueSnackbar } from "notistack"
import { useNavigate, useParams } from "react-router-dom"
import { MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateSalesAccountForm } from "../components/CreateUpdateSalesAccountForm"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { SalesAccountBase } from "../salesAccountsTypes"
import {
    salesAccountDetails,
    SalesAccountDetailsResponse,
    SalesAccountsQuery,
    updateSalesAccount,
} from "../salesAccountsApi"

function transformData(data: SalesAccountDetailsResponse) {
    return data.salesAccount
}

export function EditSalesAccount() {
    const formRef = useRef<FormikProps<SalesAccountBase>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="sales-accounts-edit-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Edit sales account"
                    page={Page.MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNT_EDIT}
                    rightElement={
                        <LocalizedStrict id="sales-accounts-form-edit-submit-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-sales-accounts">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS_ROUTE}>
                                Sales accounts
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="sales-accounts-edit-page">
                            <Breadcrumb>Edit sales account</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <SalesAccount ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const SalesAccount = forwardRef<FormikProps<SalesAccountBase>>((_, ref) => {
    const { salesAccountId } = useParams()
    const navigate = useNavigate()

    const { data: salesAccount } = useApiQuery(salesAccountDetails, {
        queryName: SalesAccountsQuery.DETAILS,
        dependencies: {
            salesAccountId: salesAccountId || "",
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(salesAccountId),
        select: transformData,
    })

    const { mutate: callUpdateSalesAccount } = useApiMutation(updateSalesAccount, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SalesAccountsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(SalesAccountsQuery.DETAILS, { departmentId: params.salesAccountId }),
            })
            navigate(MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS_ROUTE)

            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <CreateUpdateSalesAccountForm
            ref={ref}
            initialValues={prepareInitialValues(salesAccount)}
            onSubmit={(data) => {
                salesAccountId &&
                    callUpdateSalesAccount({
                        salesAccountId,
                        salesAccount: data,
                    })
            }}
        />
    )
})
